import React, { ComponentType } from 'react';
import { matchPath, RouteProps } from 'react-router-dom';
import nowTimestamp from 'shared/utils/dates/nowTimestamp';
import makeLogger from 'shared/utils/makeLogger';

import DynamicImportFailed from '../components/DynamicImportFailed';

const logger = makeLogger(__filename, { shouldLog: true });

// A lazy chunk failing to load is almost always a stale hashed-chunk URL after
// a deploy (RW-38130). We recover in two escalating steps, both guarded so we
// can never reload forever:
//   1. plain reload — picks up a fresh index.html + new chunk URLs. Fixes the
//      common Cloudflare/edge stale-HTML case.
//   2. SW reset — if a plain reload already ran and the import is STILL failing,
//      a stale service-worker / workbox precache is serving the old shell, so a
//      plain reload can't help. Unregister the SW + drop the workbox precache
//      (the same recovery the index.html watchdog uses), then reload.
// After step 2 we give up and render DynamicImportFailed.
const RELOAD_STATE_KEY = 'reader:chunk-reload-state';
const RELOAD_DEBOUNCE_MS = 30000;

type ReloadStage = 'reloaded' | 'sw-reset';
type ReloadState = { at: number; stage: ReloadStage; };

// Stored as `"<stage>|<timestamp>"` (e.g. "reloaded|1717000000000"). A flat
// string keeps parsing trivially type-safe — no `JSON.parse` returning `any`.
function readReloadState(): ReloadState | null {
  try {
    const raw = sessionStorage.getItem(RELOAD_STATE_KEY);
    if (!raw) {
      return null;
    }
    const [stage, atRaw] = raw.split('|');
    const at = Number(atRaw);
    if ((stage === 'reloaded' || stage === 'sw-reset') && Number.isFinite(at)) {
      return { at, stage };
    }
    return null;
  } catch {
    return null;
  }
}

// Returns true ONLY if the marker was durably written. We refuse to reload
// unless this succeeds, so a tab with sessionStorage blocked (private mode,
// sandboxed/embedded context, quota/security error) renders the fallback once
// instead of reloading forever — the failure mode both PR reviewers flagged.
function writeReloadState(stage: ReloadStage): boolean {
  try {
    sessionStorage.setItem(RELOAD_STATE_KEY, `${stage}|${nowTimestamp()}`);
    // Some browsers silently no-op instead of throwing; confirm it stuck.
    return sessionStorage.getItem(RELOAD_STATE_KEY) !== null;
  } catch {
    return false;
  }
}

async function resetServiceWorkerCachesAndReload(): Promise<void> {
  try {
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(
        keys.filter((key) => key.startsWith('workbox-precache')).map((key) => caches.delete(key)),
      );
    }
  } catch (error) {
    // Best effort — reload regardless so we still pick up a fresh shell.
    logger.warn('Failed to reset service worker / caches before reload', { error });
  } finally {
    window.location.reload();
  }
}

// Exported for unit tests; callers should go through `reactLazy`.
export function handleDynamicImportError<T>(
  error: unknown,
  { canReload = true }: { canReload?: boolean; } = {},
): { default: T; } {
  // NOTE: the error isn't thrown
  logger.error('Error during dynamic import', { error });

  // Don't reload when we can't (SSR), when the caller opted out (missing
  // export — a fresh bundle hits the same error), or when offline (a reload
  // won't fetch a new bundle, and we must not destroy the offline SW).
  const isOnline = typeof navigator === 'undefined' || navigator.onLine !== false;
  if (canReload && isOnline && typeof window !== 'undefined') {
    const state = readReloadState();
    const withinWindow = state !== null && nowTimestamp() - state.at <= RELOAD_DEBOUNCE_MS;

    if (!withinWindow) {
      // First failure (or the previous attempt aged out): try a cheap reload.
      if (writeReloadState('reloaded')) {
        logger.warn('Reloading to recover from dynamic import failure (likely stale chunk after deploy)', { error });
        window.location.reload();
      }
    } else if (state?.stage === 'reloaded') {
      // A plain reload didn't fix it → a stale service worker is serving the
      // old shell. Escalate to a SW/cache reset, then reload.
      if (writeReloadState('sw-reset')) {
        logger.warn('Plain reload did not recover the chunk; resetting service worker and caches', { error });
        // Errors are handled inside (it always reloads in `finally`); the catch
        // here only satisfies the floating-promise lint.
        resetServiceWorkerCachesAndReload().catch(() => {});
      }
    }
    // state.stage === 'sw-reset' within the window: both steps tried, fall
    // through to the fallback component below.
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- intentional fallback: DynamicImportFailed stands in for any component on import failure
  return { default: DynamicImportFailed as unknown as T }; // technically untrue
}

/**
 * Like `React.lazy()`, except:
 * 1. In the case of a dynamic import error, we silence it and return a fallback component.
 * 2. If the current (non-reactive) URL matches `matchingRouteProps` (i.e. when the site loads), we
 *   load the component immediately. This makes sure it's prioritized over other requests (e.g.
 *   service-worker precaching requests).
 * 3. It's a common for `matchingRouteProps` to match the props passed to a `<Route>`so we return an
 *   array containing the component and the `matchingRouteProps` (to make it easy to reuse them).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function reactLazy<T extends ComponentType<any>>({
  importer,
  exportName = 'default',
  matchingRouteProps,
}: {
  importer: () => Promise<{ [name: string]: T | unknown; }>;
  exportName?: string;
  matchingRouteProps: RouteProps | 'there-isnt-a-matching-route-and-I-know-what-I-am-doing';
}): [React.LazyExoticComponent<T>, RouteProps] {
  if (matchingRouteProps && matchPath(window.location.pathname, matchingRouteProps)) {
    logger.info('Loading lazy component immediately', {
      exportName,
      importer,
      matchingRouteProps,
    });
    importer().catch(handleDynamicImportError);
  }

  const LazyComponent = React.lazy(async () => {
    try {
      const importResult = await importer();
      const Component = importResult[exportName];
      if (!Component) {
        // Bundle loaded but the named export is missing — reloading would just
        // hit the same broken bundle, so render the fallback instead.
        return handleDynamicImportError<T>(
          new Error(`No such component (export): ${exportName}`),
          { canReload: false },
        );
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Component is T; TypeScript can't infer this from dynamic import indexing
      return { default: Component as T };
    } catch (error: unknown) {
      return handleDynamicImportError<T>(error);
    }
  });

  return [
    LazyComponent,
    typeof matchingRouteProps === 'string' ? {} : matchingRouteProps,
  ];
}
